'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Download, Upload, CheckCircle, AlertCircle, Info } from 'lucide-react';
import Topbar from '@/components/Topbar';
import Link from 'next/link';

const STORAGE_KEY = 'cashflow_sheets';
const STORAGE_ACTIVE_KEY = 'cashflow_active_sheet';
const BACKUP_VERSION = '1.0';

interface BackupData {
  version: string;
  exportedAt: string;
  cashflow_sheets: string | null;
  cashflow_active_sheet: string | null;
}

type AlertType = 'success' | 'error' | 'info';

interface AlertState {
  type: AlertType;
  message: string;
}

export default function BackupPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [sheetsCount, setSheetsCount] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [lastBackupDate, setLastBackupDate] = useState<string | null>(null);

  useEffect(() => {
    setIsMounted(true);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const sheets = JSON.parse(raw);
        setSheetsCount(sheets.length);
        const total = sheets.reduce(
          (sum: number, s: { transactions: unknown[] }) => sum + (s.transactions?.length ?? 0),
          0
        );
        setTotalTransactions(total);
      }
      const savedBackup = localStorage.getItem('cashflow_last_backup');
      if (savedBackup) setLastBackupDate(savedBackup);
    } catch {
      // ignore
    }
  }, []);

  function showAlert(type: AlertType, message: string) {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  }

  function handleBackup() {
    try {
      const backup: BackupData = {
        version: BACKUP_VERSION,
        exportedAt: new Date().toISOString(),
        cashflow_sheets: localStorage.getItem(STORAGE_KEY),
        cashflow_active_sheet: localStorage.getItem(STORAGE_ACTIVE_KEY),
      };

      const json = JSON.stringify(backup, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, '0')}${String(now.getMonth() + 1).padStart(2, '0')}${now.getFullYear()}`;
      a.download = `folhacaixa_backup_${dateStr}.json`;
      a.click();
      URL.revokeObjectURL(url);

      const isoNow = now.toISOString();
      localStorage.setItem('cashflow_last_backup', isoNow);
      setLastBackupDate(isoNow);

      showAlert('success', 'Backup efetuado com sucesso! O ficheiro foi guardado.');
    } catch {
      showAlert('error', 'Erro ao criar o backup. Tente novamente.');
    }
  }

  function handleRestoreClick() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      showAlert('error', 'Ficheiro inválido. Selecione um ficheiro .json de backup.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const data: BackupData = JSON.parse(text);

        if (!data.version || !data.exportedAt) {
          showAlert('error', 'Ficheiro de backup inválido ou corrompido.');
          return;
        }

        if (data.cashflow_sheets) {
          // Validate it's valid JSON array
          const sheets = JSON.parse(data.cashflow_sheets);
          if (!Array.isArray(sheets)) {
            showAlert('error', 'Dados de backup inválidos: folhas de caixa corrompidas.');
            return;
          }
          localStorage.setItem(STORAGE_KEY, data.cashflow_sheets);
          setSheetsCount(sheets.length);
          const total = sheets.reduce(
            (sum: number, s: { transactions: unknown[] }) => sum + (s.transactions?.length ?? 0),
            0
          );
          setTotalTransactions(total);
        }

        if (data.cashflow_active_sheet) {
          localStorage.setItem(STORAGE_ACTIVE_KEY, data.cashflow_active_sheet);
        }

        const exportedDate = new Date(data.exportedAt);
        const dateLabel = `${String(exportedDate.getDate()).padStart(2, '0')}/${String(exportedDate.getMonth() + 1).padStart(2, '0')}/${exportedDate.getFullYear()} ${String(exportedDate.getHours()).padStart(2, '0')}:${String(exportedDate.getMinutes()).padStart(2, '0')}`;
        showAlert('success', `Restore concluído com sucesso! Dados restaurados do backup de ${dateLabel}.`);
      } catch {
        showAlert('error', 'Erro ao processar o ficheiro. Certifique-se que é um backup válido do FolhaCaixa.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function formatDate(iso: string) {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Topbar />
      <main className="flex-1 w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Link href="/" className="hover:text-foreground transition-colors">Início</Link>
            <span>/</span>
            <span>Backup &amp; Restore</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-foreground tracking-tight">Backup &amp; Restore</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Guarde e restaure todos os dados da aplicação
              </p>
            </div>
            <Link
              href="/"
              className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition-colors shrink-0 shadow-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Voltar ao Início
            </Link>
          </div>
        </div>

        {/* Alert */}
        {alert && (
          <div
            className={`mb-6 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
              alert.type === 'success' ?'bg-green-50 border-green-200 text-green-800'
                : alert.type === 'error' ?'bg-red-50 border-red-200 text-red-800' :'bg-blue-50 border-blue-200 text-blue-800'
            }`}
          >
            {alert.type === 'success' && <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />}
            {alert.type === 'error' && <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
            {alert.type === 'info' && <Info className="w-4 h-4 mt-0.5 shrink-0" />}
            <span>{alert.message}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Current Data Summary */}
          <div className="md:col-span-2 bg-card border border-border rounded-xl p-5">
            <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Info className="w-4 h-4 text-muted-foreground" />
              Estado atual dos dados
            </h2>
            {isMounted ? (
              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <span className="text-muted-foreground">Folhas de caixa:</span>{' '}
                  <span className="font-semibold text-foreground">{sheetsCount}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Total de lançamentos:</span>{' '}
                  <span className="font-semibold text-foreground">{totalTransactions}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Último backup:</span>{' '}
                  <span className="font-semibold text-foreground">
                    {lastBackupDate ? formatDate(lastBackupDate) : 'Nunca'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="h-5 w-48 bg-muted rounded animate-pulse" />
            )}
          </div>

          {/* Backup Card */}
          <div className="bg-card border border-border rounded-xl p-6 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Download className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Fazer Backup</h2>
                <p className="text-xs text-muted-foreground">Exportar dados para ficheiro</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Cria um ficheiro <code className="bg-muted px-1 py-0.5 rounded text-xs">.json</code> com
              todos os dados da aplicação — folhas de caixa, lançamentos e configurações. Guarde este
              ficheiro num local seguro.
            </p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
              <li>Todas as folhas de caixa e lançamentos</li>
              <li>Configurações e tipos de operação</li>
              <li>Folha ativa selecionada</li>
            </ul>
            <button
              onClick={handleBackup}
              className="mt-auto flex items-center justify-center gap-2 w-full rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              Fazer Backup Agora
            </button>
          </div>

          {/* Restore Card */}
          <div className="bg-card border border-border rounded-xl p-6 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Upload className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Restaurar Backup</h2>
                <p className="text-xs text-muted-foreground">Importar dados de ficheiro</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Restaura os dados a partir de um ficheiro de backup previamente criado. Os dados atuais
              serão <strong>substituídos</strong> pelos dados do backup.
            </p>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                <strong>Atenção:</strong> Esta operação substitui todos os dados atuais. Recomenda-se
                fazer um backup antes de restaurar.
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              onClick={handleRestoreClick}
              className="mt-auto flex items-center justify-center gap-2 w-full rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:opacity-90 transition-colors shadow-sm"
            >
              <Upload className="w-4 h-4" />
              Selecionar Ficheiro de Backup
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-card border border-border rounded-xl p-5">
          <h2 className="text-sm font-semibold text-foreground mb-3">Como funciona</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-muted-foreground">
            <div>
              <p className="font-medium text-foreground mb-1">Backup</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Clique em &quot;Fazer Backup Agora&quot;</li>
                <li>O ficheiro <code className="bg-muted px-1 rounded">.json</code> será descarregado automaticamente</li>
                <li>Guarde o ficheiro num local seguro (nuvem, pen, etc.)</li>
              </ol>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Restore</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Clique em &quot;Selecionar Ficheiro de Backup&quot;</li>
                <li>Escolha o ficheiro <code className="bg-muted px-1 rounded">.json</code> guardado anteriormente</li>
                <li>Os dados serão restaurados imediatamente</li>
                <li>Volte ao <Link href="/" className="text-primary hover:underline">Início</Link> para ver os dados</li>
              </ol>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-border bg-card py-4">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            FolhaCaixa — Controle de caixa simplificado
          </p>
        </div>
      </footer>
    </div>
  );
}
